import React from "react";

import clsx from "clsx";
import { motion } from "framer-motion";
import { useMediaQuery } from "usehooks-ts";

import { useChromeContext } from "@components/chrome/hooks";
import { useSalesBreakdownConfigs } from "@components/data/sales-breakdown-configs";
import { GenericTooltip } from "@components/tooltip-container";
import { usePrettyLogEventPage } from "@components/v2/analytics";
import {
  DndContext,
  type DragEndEvent,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToParentElement } from "@dnd-kit/modifiers";
import { SortableContext, arrayMove } from "@dnd-kit/sortable";
import { CompareDateRangeMenu } from "@features/sales-v3/top-level-filters/components/compare-date-range-menu";
import { DateFilters } from "@features/sales-v3/top-level-filters/components/date-filters";
import { useFeatureFlags } from "@hooks/http";
import {
  DEFAULT_DATE_RANGE_CHOICE_FOR_TIME_GRANULARITY,
  type ITimeGranularityV2,
} from "@north-beam/nb-common";
import { AttributionWindowSelect } from "@pages/sales/report-body-control/attribution-window-select";
import { AttributionModelSelect } from "@shared/selects";
import { TimeGranularityV2Select } from "@shared/selects/time-granularity-v2-select";
import { generateAnonBase64Hash } from "@utils/anonymous";
import { usePrefs } from "@utils/client-side-preferences";

import {
  useAvailableBreakdownConfigs,
  usePageConfiguration,
  useSavedViews,
} from "../../hooks";
import { DateRangeSelect, SavedViewsMenu } from "../../shared";
import { useContainerWidth } from "../hooks";

import { AttributionButton } from "./attribution-button";
import { BreakdownButtonActive } from "./breakdown-button-active";
import { BreakdownButtonSelect } from "./breakdown-button-select";
import { BreakdownsButtonCompact } from "./breakdowns-button-compact";
import { FilterButtonActive } from "./filter-button-active";
import { FiltersButtonCompact } from "./filters-button-compact";

const FILTER_BUTTON_ACTIVE_WIDTH = 108;
const FILTER_LABEL_WIDTH = 100;

export const MinimizedTopBar = () => {
  const { isDemoMode } = usePrefs();
  const prettyLogEventPage = usePrettyLogEventPage();
  const { selectedSavedView } = useSavedViews();
  const { value: breakdownConfigs } = useSalesBreakdownConfigs();
  const { hasFeatureFlag } = useFeatureFlags();
  const { isSidebarExpanded } = useChromeContext();
  const {
    attributionModel,
    attributionWindow,
    accountingMode,
    timeGranularity,
    compareDateRange,
    dateRangeInterval,
    breakdowns,
    dateRange,
    filters,
    setAttributionModel,
    setAttributionWindow,
    setTimeGranularity,
    setDateRange,
    setCompareDateRange,
    setBreakdowns,
    setFilters,
  } = usePageConfiguration();

  const pageContext = {
    attributionModel,
    attributionWindow,
    accountingMode,
    timeGranularity,
    dateRangeInterval,
    dateRange,
    compareDateRange,
    breakdowns,
    filters,
  };

  const sensors = useSensors(useSensor(PointerSensor));

  const availableBreakdownConfigs = useAvailableBreakdownConfigs(breakdowns);
  const availableFilterConfigs = useAvailableBreakdownConfigs(filters);

  const [filtersWrapperElRef, filtersContainerWidth] = useContainerWidth();

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (active.id !== over?.id) {
      const oldIndex = breakdowns.findIndex(({ key }) => key === active.id);
      const newIndex = breakdowns.findIndex(({ key }) => key === over?.id);

      const newOrdering = arrayMove(breakdowns, oldIndex, newIndex);

      prettyLogEventPage({
        domain: "Minimized Top Bar",
        action: "Other",
        additionalActionInfo: "Reorder breakdowns",
        data: {
          newBreakdowns: newOrdering,
          pageContext,
        },
      });

      setBreakdowns(newOrdering);
    }
  };

  const isAtSupportedWidthForDateFilters = useMediaQuery("(max-width: 1500px)");
  const isAtSupportedWidthForBreakdowns = useMediaQuery("(max-width: 1700px)");
  const areBreakdownsSelected = breakdowns.length > 0;
  const shouldCompactBreakdowns =
    (breakdowns.length > 3 && filters.length > 1) ||
    isAtSupportedWidthForBreakdowns;
  const areFiltersSelected = filters.length > 0;
  const showCompactedFilters =
    filters.length * FILTER_BUTTON_ACTIVE_WIDTH + FILTER_LABEL_WIDTH >
    filtersContainerWidth;

  const isAttributionWindowVisible = accountingMode === "accrual";
  const isHourlyTimeGranularity = timeGranularity === "hourly";
  const positionTop = hasFeatureFlag("enableDashboardV2") ? 76 : 56;
  const sidebarWidth = hasFeatureFlag("enableDashboardV2")
    ? isSidebarExpanded
      ? 240
      : 73
    : 56;

  return (
    <motion.div
      key="minimized-top-bar"
      initial={{ translateY: -positionTop }}
      exit={{ translateY: -positionTop }}
      animate={{ translateY: 0 }}
      transition={{ duration: 0.2 }}
      className={clsx(
        "bg-white border-b border-gray py-1 px-2 flex items-center justify-between fixed z-30",
      )}
      style={{ top: positionTop, width: `calc(100% - ${sidebarWidth}px)` }}
    >
      <div className="flex items-center gap-1">
        <h3 className="text-lg font-bold m-0 max-w-xxxs truncate">
          {generateAnonBase64Hash({
            value: selectedSavedView?.name,
            prefix: "quick-filter",
            doAnon: isDemoMode && !selectedSavedView?.is_template,
          })}
        </h3>
        <SavedViewsMenu
          buttonClassName={clsx(
            "rounded-full border h-7 w-7 text-xs font-bold flex items-center justify-center",
          )}
          placement="bottom-start"
        />
        <div>
          <AttributionModelSelect
            menuClassName="w-[200px]"
            attributionModel={attributionModel}
            onChange={(value) => {
              const newAttributionModel = value;
              prettyLogEventPage({
                domain: "Minimized Top Bar",
                action: "onSelect",
                additionalActionInfo: "Set Attribution Model",
                data: {
                  newAttributionModel,
                  pageContext,
                },
              });

              setAttributionModel(newAttributionModel);
            }}
            label=""
            buttonRenderer={(props) => (
              <AttributionButton variant="combobox" label="Model" {...props} />
            )}
          />
        </div>
        {isAttributionWindowVisible && (
          <div>
            <GenericTooltip
              className={clsx(isHourlyTimeGranularity && "cursor-not-allowed")}
              content={
                isHourlyTimeGranularity ? (
                  <>
                    Attribution window selection unavailable
                    <br />
                    for the time granularity selected
                  </>
                ) : null
              }
              placement="bottom"
              noInfoCircle
            >
              <AttributionWindowSelect
                menuClassName="w-[100px]"
                attributionModel={attributionModel}
                attributionWindow={attributionWindow}
                timeGranularity="daily"
                onChange={(value) => {
                  const newAttributionWindow = value;

                  prettyLogEventPage({
                    domain: "Minimized Top Bar",
                    action: "onSelect",
                    additionalActionInfo: "Set Attribution Window",
                    data: {
                      pageContext,
                      newAttributionWindow,
                    },
                  });

                  setAttributionWindow(newAttributionWindow);
                }}
                label=""
                buttonRenderer={(props) => (
                  <AttributionButton
                    isDisabled={isHourlyTimeGranularity}
                    variant="combobox"
                    label="Window"
                    {...props}
                  />
                )}
              />
            </GenericTooltip>
          </div>
        )}
        {isAtSupportedWidthForDateFilters ? (
          <DateRangeSelect
            timeGranularity={timeGranularity}
            dateRange={dateRange}
            compareDateRange={compareDateRange}
            onTimeGranularityUpdate={(value) => {
              prettyLogEventPage({
                domain: "Minimized Top Bar",
                action: "onSelect",
                additionalActionInfo: "Set Time Granularity",
                data: {
                  newTimeGranularity: value,
                  pageContext,
                },
              });

              setTimeGranularity(value);
            }}
            onDateRangeUpdate={(value) => {
              prettyLogEventPage({
                domain: "Minimized Top Bar",
                action: "onSelect",
                additionalActionInfo: "Set Date Range",
                data: {
                  newDateRange: value,
                  pageContext,
                },
              });

              setDateRange(value);
            }}
            onCompareDateRangeUpdate={(value) => {
              prettyLogEventPage({
                domain: "Minimized Top Bar",
                action: "onSelect",
                additionalActionInfo: "Set Compare Date Range",
                data: {
                  newCompareDateRange: value,
                  pageContext,
                },
              });

              setCompareDateRange(value);
            }}
          />
        ) : (
          <>
            <TimeGranularityV2Select
              label=""
              className="text-xs"
              timeGranularity={timeGranularity}
              onChange={(value: ITimeGranularityV2) => {
                prettyLogEventPage({
                  domain: "Top Level Filters",
                  action: "onChange",
                  additionalActionInfo: "Set Time Granularity",
                  data: {
                    newTimeGranularity: value,
                    pageContext,
                  },
                });

                setDateRange(
                  DEFAULT_DATE_RANGE_CHOICE_FOR_TIME_GRANULARITY[value],
                );
                setTimeGranularity(value);
              }}
              menuClassName="w-[100px]"
              width="fit-content"
              icon={null}
              buttonRenderer={(props) => (
                <AttributionButton variant="combobox" label="" {...props} />
              )}
            />
            <DateFilters className="h-7" />
            <CompareDateRangeMenu
              className="text-xs h-7"
              timeGranularity={timeGranularity}
              compareDateRange={compareDateRange}
              onCompareDateRangeUpdate={(value) => {
                prettyLogEventPage({
                  domain: "Top Level Filters",
                  action: "onChange",
                  additionalActionInfo: "Set Compare Date Range",
                  data: {
                    newCompareDateRange: value,
                    pageContext,
                  },
                });

                setCompareDateRange(value);
              }}
            />
          </>
        )}
      </div>
      <div className="flex items-stretch gap-2 flex-1">
        <div
          ref={filtersWrapperElRef}
          className="flex items-center justify-end gap-1 flex-1"
        >
          {areFiltersSelected && !showCompactedFilters && (
            <div className="text-xs text-gray">FILTERS</div>
          )}
          {filters.length > 0 && showCompactedFilters && (
            <FiltersButtonCompact
              filters={filters}
              onUpdate={(filter) => {
                const newFilters = filters.map((e) =>
                  e.key === filter.key ? filter : e,
                );

                prettyLogEventPage({
                  domain: "Minimized Top Bar",
                  action: "onClick",
                  additionalActionInfo: "Set filters",
                  data: {
                    newFilters,
                    pageContext,
                  },
                });

                setFilters(newFilters);
              }}
              onDelete={(filter) => {
                const newFilters = filters.filter((e) => e.key !== filter.key);

                prettyLogEventPage({
                  domain: "Minimized Top Bar",
                  action: "onClick",
                  additionalActionInfo: "Delete filter",
                  data: {
                    newFilters,
                    pageContext,
                  },
                });

                setFilters(newFilters);
              }}
            />
          )}
          {!showCompactedFilters &&
            filters.map((e) => (
              <FilterButtonActive
                key={e.key}
                breakdown={e}
                onUpdate={(values) => {
                  const newFilters = filters.map((v) =>
                    v.key === e.key ? { key: v.key, values } : v,
                  );

                  prettyLogEventPage({
                    domain: "Minimized Top Bar",
                    action: "onClick",
                    additionalActionInfo: "Set filters",
                    data: {
                      newFilters,
                      pageContext,
                    },
                  });

                  setFilters(newFilters);
                }}
                onDelete={() => {
                  const newFilters = filters.filter((v) => v.key !== e.key);

                  prettyLogEventPage({
                    domain: "Minimized Top Bar",
                    action: "onClick",
                    additionalActionInfo: "Delete filters",
                    data: {
                      newFilters,
                      pageContext,
                    },
                  });

                  setFilters(newFilters);
                }}
              />
            ))}
          {availableFilterConfigs.length > 0 && (
            <BreakdownButtonSelect
              buttonText="ADD FILTERS"
              breakdownConfigs={availableFilterConfigs}
              areBreakdownsSelected={areFiltersSelected}
              onChange={(value) => {
                const newFilters = [...filters, value];

                prettyLogEventPage({
                  domain: "Minimized Top Bar",
                  action: "onClick",
                  additionalActionInfo: "Set filters",
                  data: {
                    newFilters,
                    pageContext,
                  },
                });

                setFilters(newFilters);
              }}
            />
          )}
        </div>
        <div className="border-r my-0.5"></div>
        {shouldCompactBreakdowns ? (
          <BreakdownsButtonCompact />
        ) : (
          <div className="flex items-center gap-2">
            {areBreakdownsSelected && (
              <div className="text-xs text-gray ml-2">BREAKDOWNS</div>
            )}
            {areBreakdownsSelected && (
              <div className="flex gap-1">
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  modifiers={[restrictToParentElement]}
                  onDragEnd={handleDragEnd}
                  autoScroll={false}
                >
                  <SortableContext items={breakdowns.map(({ key }) => key)}>
                    {breakdowns.map((e, idx) => (
                      <BreakdownButtonActive
                        key={e.key}
                        breakdownConfigs={[
                          ...availableBreakdownConfigs,
                          breakdownConfigs.find((v) => v.key === e.key)!,
                        ].filter((e) => e)}
                        breakdown={e}
                        position={idx + 1}
                        showPosition={breakdowns.length > 1}
                        onChange={(newValue) => {
                          const newBreakdowns = breakdowns.map((v) =>
                            v.key === e.key ? newValue : v,
                          );

                          prettyLogEventPage({
                            domain: "Minimized Top Bar",
                            action: "onSelect",
                            additionalActionInfo: "Set Breakdowns",
                            data: {
                              newBreakdowns,
                              pageContext,
                            },
                          });

                          setBreakdowns(newBreakdowns);
                        }}
                        onDelete={() => {
                          const newBreakdowns = breakdowns.filter(
                            (v) => v.key !== e.key,
                          );

                          prettyLogEventPage({
                            domain: "Minimized Top Bar",
                            action: "onSelect",
                            additionalActionInfo: "Delete breakdown",
                            data: {
                              newBreakdowns,
                              pageContext,
                            },
                          });

                          setBreakdowns(newBreakdowns);
                        }}
                      />
                    ))}
                  </SortableContext>
                </DndContext>
              </div>
            )}
            {availableBreakdownConfigs.length > 0 && (
              <BreakdownButtonSelect
                buttonText="ADD BREAKDOWNS"
                breakdownConfigs={availableBreakdownConfigs}
                areBreakdownsSelected={areBreakdownsSelected}
                onChange={(value) => {
                  const newBreakdowns = [...breakdowns, value];

                  prettyLogEventPage({
                    domain: "Minimized Top Bar",
                    action: "onSelect",
                    additionalActionInfo: "Add breakdowns",
                    data: {
                      newBreakdowns,
                      pageContext,
                    },
                  });

                  setBreakdowns(newBreakdowns);
                }}
              />
            )}
          </div>
        )}
      </div>
    </motion.div>
  );
};
